import React from 'react';
import { observer } from 'mobx-react';
import { AudioBridgeConnectionFormVm } from './audio-bridge-connection.form-vm';
import { ConnectionTypeField } from '../common-fields/connection-type.field';
import { AudioBridgeConnectionField as FieldKey, FORM_CONFIG, customAudioProviderOption, AudioBridgeConnectionField } from './audio-bridge-connection.form-config';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormFieldInput } from '../../../../../../../global/form/form-field-input/form-field-input';
import { PARTICIPANT_TELEPHONE_NUMBERS_HELP_TEXT } from '../../../../../_connections/connections.texts';
import { hasPermission } from '../../../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../../../api/roles.dao';
import { LOADING } from '../../../../../../../../constants/common-labels.constants';
import { ConnectionFormVM } from '../../connection-form.viewmodel';
import Spinner from '../../../../../../../global/spinner/spinner';
import { WarningIcon } from '../../../../../../../global/icons/icons';
import { AUDIO_BRIDGE_ERROR_MESSAGE, AUDIO_BRIDGE_SUCCESS_MESSAGE } from '../../../../../../../../constants/audio-bridge-connection.constant';
import CheckMarkSVG from '../../../../../../../../images/svg/icons8-checkmark';
import { FormField } from '../../../../../../../global/form/form-field/form-field';
import { CopyButton } from '../../../../../../../global/copy-button/copy-button';
import { FormFieldError } from '../../../../../../../global/form/form-field-input/form-field-error';

export const AudioBridgeConnectionFormBody = observer((props: {
    vm: AudioBridgeConnectionFormVm;
    isMeetingForm: boolean;
    formVM: ConnectionFormVM;
}): JSX.Element => {
    const intl = useIntl();
    const { vm, isMeetingForm, formVM } = props;

    const {
        label,
        audioProviderId,
        provider,
        participantTelephoneNumbers,
        participantOneTapDial,
        conferenceId,
        participantId,
        connectionPassword: passcode,
        operatorTelephoneNumber,
        operatorPin,
        hostLink: operatorConsoleLink,
        connectionNotes,
        audioBridgeID,
        audioBridgeName,
        meetingUniqueReference
    } = vm.getFormFields(FieldKey);

    const hostManagementPermission = hasPermission(Permission.HOST_MANAGEMENT);
    const isCustomParticipantTelephoneNumbersSelected = audioProviderId.value === customAudioProviderOption.value;

    const handleConferenceIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        vm.setValue(FieldKey.CONFERENCE_ID, event.target.value);
    };

    const handleConferenceIdBlur = (): void => {
        vm.setValueTouched(AudioBridgeConnectionField.CONFERENCE_ID);
        formVM.getAudioBridgeDetails(AudioBridgeConnectionField.CONFERENCE_ID, conferenceId.value as string);
    };

    return (
        <form className='oe-form'>
            {vm.isLoading
                ? <FormattedMessage
                    {...LOADING}
                    values={{
                        suffix: '...'
                    }}
                />
                : <div className='oe-form__body'>
                    <ConnectionTypeField value={vm.type} />

                    <FormFieldInput.Text
                        field={label}
                        labelDescriptor={FORM_CONFIG[FieldKey.LABEL].labelDescriptor}
                        withError
                        withCopyBtn
                    />

                    {isMeetingForm && <FormFieldInput.Select
                        field={meetingUniqueReference}
                        labelDescriptor={FORM_CONFIG[FieldKey.MEETING_UNIQUE_REFERENCE].labelDescriptor}
                        customOptions={
                            FORM_CONFIG[FieldKey.MEETING_UNIQUE_REFERENCE].valueOptions.map((item, i) => {
                                const isObject = typeof item === 'object';
                                return <option value={isObject ? item.value : item} key={i}>{isObject ? item.label : item}</option>;
                            })
                        }
                        fieldClassName='m-bottom-20px'
                    />}

                    <FormFieldInput.Select
                        field={audioProviderId}
                        labelDescriptor={FORM_CONFIG[FieldKey.AUDIO_PROVIDER_ID].labelDescriptor}
                        options={vm.getTelephoneNumberSetOptions()}
                        customChangeAction={vm.selectTelephoneNumberSet.bind(vm)}
                        optionsSeparator={[0]}
                        fieldClassName='m-bottom-20px oe-form__select-padding'
                        data-test-id='audio-provider-name'
                        placeholderOption
                        placeholderOptionSeparator
                        withError
                    />

                    {isCustomParticipantTelephoneNumbersSelected
                        && <>
                            <FormFieldInput.Textarea
                                field={participantTelephoneNumbers}
                                labelDescriptor={FORM_CONFIG[FieldKey.PARTICIPANT_TELEPHONE_NUMBERS].labelDescriptor}
                                infoText={intl.formatMessage(PARTICIPANT_TELEPHONE_NUMBERS_HELP_TEXT)}
                                data-test-id='participant-telephone-numbers'
                                withError
                                withCopyBtn
                            />

                            <FormFieldInput.Text
                                field={participantOneTapDial}
                                labelDescriptor={FORM_CONFIG[FieldKey.PARTICIPANT_ONE_TAP_DIAL].labelDescriptor}
                                data-test-id='participant-one-tap-dial'
                                withError
                                withCopyBtn
                            />
                        </>
                    }

                    <FormFieldInput.Select
                        field={provider}
                        labelDescriptor={FORM_CONFIG[FieldKey.PROVIDER].labelDescriptor}
                        options={vm.getProviderOptions()}
                        fieldClassName='m-bottom-20px oe-form__select-padding'
                        disabled={audioProviderId.value && !isCustomParticipantTelephoneNumbersSelected}
                        data-test-id='provider'
                        placeholderOption
                    />

                    <FormFieldInput.Text
                        field={audioBridgeName}
                        labelDescriptor={FORM_CONFIG[FieldKey.AUDIO_BRIDGE_NAME].labelDescriptor}
                        withError
                        withCopyBtn
                    />

                    <FormFieldInput.Text
                        field={audioBridgeID}
                        labelDescriptor={FORM_CONFIG[FieldKey.AUDIO_BRIDGE_ID].labelDescriptor}
                        withError
                        withCopyBtn
                    />

                    <FormField
                        className='m-bottom-20px'
                        label={<FormattedMessage {...FORM_CONFIG[FieldKey.CONFERENCE_ID].labelDescriptor} />}
                    >
                        <div className='flex'>
                            <input
                                className='oe-form-field__control'
                                value={conferenceId.value as string}
                                type='text'
                                onChange={handleConferenceIdChange}
                                onBlur={!formVM.initialValues?.connectionId && handleConferenceIdBlur}
                            />
                            <CopyButton copyValue={conferenceId.value} className='m-left-10px' disabled={!conferenceId.value} />
                        </div>
                        <FormFieldError field={conferenceId} labelDescriptor={FORM_CONFIG[FieldKey.CONFERENCE_ID].labelDescriptor} />
                    </FormField>

                    {formVM.isMeetingConnectionsForm
                        && !formVM.isEdit()
                        && !vm.meetingConnectionId
                        && !formVM.initialValues?.connectionId &&
                        <>
                            {formVM.loadAudioBridgeDetails ? (
                                <div className='__loading'>
                                    <Spinner />
                                </div>
                            ) : formVM.audioBridgeConnectionStatus === false ? (
                                <div className='m-bottom-10px flex center'>
                                    <div className='connection-form__warning-icon'>
                                        <WarningIcon className='warning m-right-10px' />
                                    </div>
                                    <div>{AUDIO_BRIDGE_ERROR_MESSAGE}</div>
                                </div>
                            ) : formVM.audioBridgeConnectionStatus === true ? (
                                <div className='m-bottom-10px flex center'>
                                    <div className='connection-form__check-mark-icon m-right-10px'>
                                        <CheckMarkSVG />
                                    </div>
                                    <div className='success-container'>{AUDIO_BRIDGE_SUCCESS_MESSAGE}</div>
                                </div>
                            ) : null}
                        </>
                    }

                    <FormFieldInput.Text
                        field={participantId}
                        labelDescriptor={FORM_CONFIG[FieldKey.PARTICIPANT_ID].labelDescriptor}
                        withError
                        withCopyBtn
                    />

                    <FormFieldInput.Text
                        field={passcode}
                        labelDescriptor={FORM_CONFIG[FieldKey.PASSCODE].labelDescriptor}
                        withError
                        withCopyBtn
                    />

                    <FormFieldInput.Text
                        field={operatorTelephoneNumber}
                        labelDescriptor={FORM_CONFIG[FieldKey.OPERATOR_TELEPHONE_NUMBER].labelDescriptor}
                        withError
                        withCopyBtn
                    />

                    <FormFieldInput.Text
                        field={operatorPin}
                        labelDescriptor={FORM_CONFIG[FieldKey.OPERATOR_PIN].labelDescriptor}
                        withError
                        withCopyBtn
                    />

                    {hostManagementPermission && <FormFieldInput.Text
                        field={operatorConsoleLink}
                        labelDescriptor={FORM_CONFIG[FieldKey.OPERATOR_CONSOLE_LINK].labelDescriptor}
                        disabled={true}
                        withError
                        withCopyBtn
                    />}

                    <FormFieldInput.Textarea
                        field={connectionNotes}
                        labelDescriptor={FORM_CONFIG[FieldKey.CONNECTION_NOTES].labelDescriptor}
                        withError
                        withCopyBtn
                    />
                </div>
            }
        </form>
    );
});
